import {Chip, LinearProgress, Stack, Typography} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import ScheduleIcon from '~/icons/svg/ScheduleIcon.svg?react'
import WarningCircleIcon from '~/icons/svg/WarningCircleIcon.svg?react'
import InfoIcon from '~/icons/svg/InfoIcon.svg?react'
import type {
    BuQualification,
    ComplianceBuAggregate,
} from "~/pages/supplier-360/types/Supplier360Types";
import {
    BU_COMPLIANCE_STATUSES,
    QUALIFICATION_STATUSES,
} from "~/pages/supplier-360/types/Supplier360Types";
import {
    BU_STATUS_LABEL,
    QUALIFICATION_COLOR,
    QUALIFICATION_LABEL,
} from "~/pages/supplier-360/tabs/SupplierCompliance/constants/complianceLabels";
import {EMPTY_CELL} from "~/components/data-grid/cells/gridCellFormat";
import {
    centered,
    emptyCell,
    subLine,
} from "~/components/data-grid/cells/gridCells";
import {BU_STATUS_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceBuStatusChips";
import {requalLine} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceCells";

/**
 * A Business units row: the BU qualification (§2.7) plus its requirement counts from the grouped
 * records grid (§2.8a). `aggregate` is undefined while those load, or when the BU has no records.
 */
export type ComplianceBuRow = BuQualification & {
    aggregate: ComplianceBuAggregate | undefined;
};

const ICON_SIZE = {width: 14, height: 14};

/** "Needs attention" (`SxcBuGrid`): one chip per non-zero bucket — unmet, expiring, pending. */
function attentionChips(aggregate: ComplianceBuAggregate) {
    const chips = [
        aggregate.unmet_agg > 0 && (
            <Chip key="unmet" size="small" color="error" label={`${aggregate.unmet_agg} unmet`} icon={<WarningCircleIcon {...ICON_SIZE}/>}/>
        ),
        aggregate.expiring_agg > 0 && (
            <Chip key="expiring" size="small" color="warning" label={`${aggregate.expiring_agg} expiring`} icon={<ScheduleIcon {...ICON_SIZE}/>}/>
        ),
        aggregate.pending_agg > 0 && (
            <Chip key="pending" size="small" color="info" label={`${aggregate.pending_agg} pending`} icon={<InfoIcon {...ICON_SIZE}/>}/>
        ),
    ].filter(Boolean);

    if (chips.length === 0) return emptyCell();
    return centered(<Stack direction="row" gap={0.5} sx={{flexWrap: 'wrap'}}>{chips}</Stack>);
}

/**
 * The Business units lens (`sup-compliance-bu.jsx`), one row per `bu_qualification` (§2.7).
 * Client-side over the already-fetched unpaginated list, so sorting and filtering run locally.
 * The count columns read the row's `aggregate`; a BU with none shows empty cells.
 */
export function getComplianceBuColumns(): GridColDef<ComplianceBuRow>[] {
    return [
        {
            field: 'business_unit',
            headerName: 'Business unit',
            flex: 1.4,
            minWidth: 220,
            /** Nested `BusinessUnit` (§2.7) — sort / filter on its name. `null` for an unmatched BU. */
            valueGetter: (_value, row) => row.business_unit?.bu_name ?? EMPTY_CELL,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2" fontWeight={600} noWrap>{row.business_unit?.bu_name ?? EMPTY_CELL}</Typography>
                    {subLine(row.region)}
                </>,
            ),
        },
        {
            field: 'category',
            headerName: 'Category scope',
            flex: 1.2,
            minWidth: 190,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2" noWrap>{row.category}</Typography>
                    {subLine(`${row.risk_class} risk class`)}
                </>,
            ),
        },
        {
            /** `total_agg` (§2.8a). The company-wide / business-unit split isn't in the API — sub-line empty. */
            field: 'applicable_requirements',
            headerName: 'Applicable requirements',
            type: 'number',
            flex: 1,
            minWidth: 190,
            valueGetter: (_value, row) => row.aggregate?.total_agg ?? null,
            renderCell: ({row}) => row.aggregate
                ? centered(
                    <Stack sx={{alignItems: 'flex-end', width: '100%'}}>
                        <Typography variant="body2" fontWeight={600}>{row.aggregate.total_agg}</Typography>
                        {subLine(EMPTY_CELL)}
                    </Stack>,
                )
                : emptyCell(),
        },
        {
            /** `met_agg` of `total_agg` (§2.8a) as the design's coverage bar. */
            field: 'evidence_coverage',
            headerName: 'Evidence coverage',
            flex: 1,
            minWidth: 170,
            filterable: false,
            valueGetter: (_value, row) => row.aggregate?.met_agg ?? null,
            renderCell: ({row}) => row.aggregate && row.aggregate.total_agg > 0
                ? centered(
                    <Stack gap={0.5} sx={{width: '100%'}}>
                        <Stack direction="row" gap={1} sx={{alignItems: 'center'}}>
                            <LinearProgress
                                variant="determinate"
                                value={(row.aggregate.met_agg / row.aggregate.total_agg) * 100}
                                sx={{flex: 1, height: 7, borderRadius: 4}}
                            />
                            <Typography variant="caption" fontWeight={600}>
                                {`${row.aggregate.met_agg}/${row.aggregate.total_agg}`}
                            </Typography>
                        </Stack>
                        {subLine(`${row.aggregate.met_agg} of ${row.aggregate.total_agg} records met`)}
                    </Stack>,
                )
                : emptyCell(),
        },
        {
            field: 'qualification_status',
            headerName: 'Qualification',
            type: 'singleSelect',
            valueOptions: QUALIFICATION_STATUSES.map(value => ({value, label: QUALIFICATION_LABEL[value]})),
            flex: 1,
            minWidth: 160,
            renderCell: ({row}) => centered(
                <Stack gap={0.5}>
                    <Chip
                        size="small"
                        color={QUALIFICATION_COLOR[row.qualification_status]}
                        label={QUALIFICATION_LABEL[row.qualification_status]}
                    />
                    {requalLine(row.requalification_due)}
                </Stack>,
            ),
        },
        {
            field: 'status',
            headerName: 'Compliance status',
            type: 'singleSelect',
            valueOptions: BU_COMPLIANCE_STATUSES.map(value => ({value, label: BU_STATUS_LABEL[value]})),
            flex: 1,
            minWidth: 160,
            renderCell: ({row}) => centered(BU_STATUS_CHIP[row.status]),
        },
        {
            /** `unmet_agg` / `expiring_agg` / `pending_agg` (§2.8a). */
            field: 'needs_attention',
            headerName: 'Needs attention',
            flex: 1.2,
            minWidth: 220,
            sortable: false,
            filterable: false,
            renderCell: ({row}) => row.aggregate ? attentionChips(row.aggregate) : emptyCell(),
        },
    ];
}
